import {useMemo} from "react";

import type {StyleType} from "@khanacademy/wonder-blocks-core";
import {ShimmerRectangle} from "@khan/component-shimmer";
import {usePrefersReducedMotion} from "@khan/data-prefers-reduced-motion";

import SimpleTable from "./simple-table.tsx";
import type {Column} from "./types.ts";

interface Props<T> {
    /**
     * The number of rows to render in the loading table.
     */
    numRows: number;
    /**
     * The columns to include in the table.
     */
    columns: Array<Column<T>>;
    /**
     * The caption associated with the table. This is used to describe the table
     * to screen readers.
     */
    caption?: string;
    /**
     * Style for the table.
     */
    tableStyle?: StyleType;
    /**
     * Style for the rows.
     */
    rowStyle?: StyleType;
    /**
     * Style for the cells.
     */
    cellStyles?: StyleType;
}

export const LoadingTable = <T,>({
    numRows,
    columns,
    caption,
    tableStyle,
    rowStyle,
    cellStyles,
}: Props<T>) => {
    const prefersReducedMotion = usePrefersReducedMotion();

    // Render a shimmer rectangle for each column in a row.
    const loadingColumns: Array<Column<T>> = useMemo(
        () =>
            columns.map((col) => ({
                ...col,
                // Disable sorting for loading state
                sortable: undefined,
                cell: () => (
                    <ShimmerRectangle animated={!prefersReducedMotion} />
                ),
            })),
        [columns, prefersReducedMotion],
    );

    // Create placeholder rows; their values are unused since cells are overridden.
    const loadingData: Array<T> = useMemo(
        () => Array.from({length: numRows}, () => ({}) as T),
        [numRows],
    );

    return (
        <SimpleTable
            columns={loadingColumns}
            data={loadingData}
            caption={caption}
            tableStyle={tableStyle}
            rowStyle={rowStyle}
            cellStyles={cellStyles}
            isTableLoading={true}
        />
    );
};
